import { FC, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { millisToDateTime, millisToTime } from "@/services/dateTimeService";
import { useGameDataContext } from "@context/GameDataContext";
import { RocketLaunch, Schedule } from "@mui/icons-material";
import { useResponsiveContext } from "@context/ResponsiveContext";

const availableIcons = {
  rocketLaunch: RocketLaunch,
  schedule: Schedule,
};

const ClockAndStartTime: FC = () => {
  const { onTablet } = useResponsiveContext();
  const { game } = useGameDataContext();
  const [currentTimestamp, setCurrentTimestamp] = useState(Date.now());
  const [output, setOutput] = useState("-");
  const [secondary, setSecondary] = useState<string>();
  const [icon, setIcon] = useState<keyof typeof availableIcons>();
  const [textAlign, setTextAlign] = useState<"right" | "center">("right");

  useEffect(() => {
    const timer = setInterval(
      () => {
        setCurrentTimestamp(Date.now());
      },
      (game?.gameData.gameStartDateTime || 0) < Date.now() ? 1000 : 100,
    );
    return () => {
      clearInterval(timer);
    };
  }, [game]);

  useEffect(() => {
    if (!game) {
      setIcon(undefined);
      setOutput("-");
      setSecondary(undefined);
    } else {
      const startTime = game?.gameData.gameStartDateTime;

      if (
        new Date(startTime).toDateString() ===
        new Date(currentTimestamp).toDateString()
      ) {
        if (startTime > currentTimestamp) {
          if (startTime < currentTimestamp + 30 * 60 * 1000) {
            // 30 min before game
            const minutesLeft = (
              (startTime - currentTimestamp) /
              (60 * 1000)
            ).toFixed(0);
            setIcon("rocketLaunch");
            setOutput(`${millisToTime(startTime)} Uhr`);
            setSecondary(`(t -${minutesLeft}min)`);
          } else {
            // game is today
            setIcon("rocketLaunch");
            setOutput(`${millisToTime(startTime)} Uhr`);
            setSecondary("(heute)");
          }
        } else {
          // game has started
          setIcon("schedule");
          setOutput(`${millisToTime(currentTimestamp, true)} Uhr`);
          setSecondary(undefined);
        }
      } else {
        // game is tomorrow or later
        setIcon(undefined);
        setOutput(millisToDateTime(startTime));
        setSecondary(undefined);
      }
    }
  }, [currentTimestamp, game]);

  useEffect(() => {
    setTextAlign(onTablet ? "center" : "right");
  }, [onTablet]);

  const IconWrapper: FC<{ icon: keyof typeof availableIcons }> = ({ icon }) => {
    const Component = availableIcons[icon];
    return <Component />;
  };

  return (
    <Box
      display={"flex"}
      justifyContent={textAlign}
      flexWrap={"wrap"}
      columnGap={1}
    >
      <Box display={"flex"} gap={1} justifyContent={textAlign}>
        {icon && <IconWrapper icon={icon} />}
        <Typography align={textAlign}>{output}</Typography>
      </Box>
      {secondary && (
        <Box display={"flex"} gap={1} justifyContent={textAlign}>
          <Typography align={onTablet ? "center" : "right"}>
            {secondary}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ClockAndStartTime;
